<script lang="ts">
  import { getToastStore } from '@skeletonlabs/skeleton'
  import JSON5 from 'json5'
  import Papa from 'papaparse'
  import { SvelteSet } from 'svelte/reactivity'

  import Icon from '$lib/components/Icon.svelte'

  export interface Props {
    value?: Record<string, number> | Record<number, string> | string[] | null
    readOnly?: boolean
  }

  let { value = $bindable(null), readOnly = false }: Props = $props()

  // Internal editable rows
  type RowInput = { id: number; label: string }
  type Row = RowInput & { key: number }

  let nextRowKey = 0

  const createRow = (rowData: RowInput): Row => ({ ...rowData, key: nextRowKey++ })

  function toRows(
    valueToConvert:
      | Record<string, number>
      | Record<number, string>
      | Record<string, string>
      | string[]
      | null,
  ): Row[] {
    nextRowKey = 0
    if (!valueToConvert) {
      return []
    }
    if (Array.isArray(valueToConvert)) {
      return valueToConvert.map((label, id) => createRow({ id, label }))
    }
    const entries = Object.entries(valueToConvert)
    const allNumberValues = entries.every(
      ([, maybeNumberValue]) => typeof maybeNumberValue === 'number',
    )
    if (allNumberValues) {
      // Treat as { label: id }
      return entries.map(([label, idVal]) => createRow({ id: Number(idVal), label }))
    }
    // Fallback: { id: label }
    return entries.map(([idStr, label]) =>
      createRow({ id: Number.parseInt(idStr, 10), label: String(label) }),
    )
  }

  let rows: Row[] = $state([])
  let isInternalUpdate = false

  // Sync rows with value changes from external source only
  $effect(() => {
    if (!isInternalUpdate) {
      rows = toRows(value)
    }
  })

  export const validateRows = (rowsToValidate: RowInput[]) => {
    const allNumberValues = rowsToValidate.every((row) => Number.isFinite(row.id))
    if (!allNumberValues) {
      throw new Error('All rows must have a number id')
    }
    const seenIds = new SvelteSet<number>()
    for (const row of rowsToValidate) {
      if (seenIds.has(row.id)) {
        throw new Error(`Duplicate ID found: ${row.id}. All IDs must be unique.`)
      }
      seenIds.add(row.id)
    }
  }

  // Validate the current internal rows state
  export const validate = () => {
    try {
      validateRows(rows)
      return { valid: true, error: null }
    } catch (err) {
      return { valid: false, error: err instanceof Error ? err.message : String(err) }
    }
  }

  const toJson = (): Record<string, number> => {
    const jsonObject: Record<string, number> = {}
    for (const row of rows) {
      if (Number.isFinite(row.id) && row.label.trim()) {
        jsonObject[row.label.trim()] = row.id
      }
    }
    return jsonObject
  }

  const addRow = () => {
    const nextId = rows.length ? Math.max(...rows.map((row) => row.id)) + 1 : 0
    rows = [...rows, createRow({ id: nextId, label: '' })]
    // Don't update value yet - wait until user fills in the label
  }
  const removeRow = (index: number) => {
    rows = rows.toSpliced(index, 1)
    syncValue()
  }

  const toastStore = getToastStore()

  let pastedText = $state('')
  const parseCommaSeparated = () => {
    try {
      const parsed = Papa.parse<string[]>(pastedText, {
        delimiter: ',',
        skipEmptyLines: true,
      })
      const allCsvEntries = (parsed.data || [])
        .flat()
        .map((csvEntry) => (csvEntry ?? '').trim())
        .filter((csvEntry) => csvEntry.length > 0)
      rows = toRows(allCsvEntries)
      syncValue()
    } catch (err) {
      console.error('Invalid CSV pasted for class mapping', err)
      toastStore.trigger({
        message: 'Invalid CSV pasted for class mapping',
        background: 'variant-filled-error',
      })
    }
  }
  const parseDictionary = () => {
    try {
      const json5Object = JSON5.parse(pastedText) as Record<string, string> | string[]
      const nextRows = toRows(json5Object)
      validateRows(nextRows)
      rows = nextRows
      syncValue()
    } catch (err) {
      console.error('Invalid JSON/Python dict pasted for class mapping', err)
      toastStore.trigger({
        message: 'Invalid JSON/Python dict pasted for class mapping',
        background: 'variant-filled-error',
      })
    }
  }

  const onCellChange = (index: number, key: 'id' | 'label', newValue: string) => {
    const rowChanged = rows[index]
    if (key === 'id') {
      const number = Number(newValue)
      if (!Number.isNaN(number)) {
        rowChanged.id = number
      }
    } else {
      rowChanged.label = newValue
    }
    rows = rows.toSpliced(index, 1, rowChanged)
  }

  const syncValue = () => {
    isInternalUpdate = true
    value = toJson()
    // Use queueMicrotask to ensure the effect sees isInternalUpdate=true
    queueMicrotask(() => {
      isInternalUpdate = false
    })
  }
</script>

<section class="space-y-2">
  <p>Class mapping (optional):</p>
  {#if readOnly}
    <div class="rounded border border-surface-300/20 p-2 text-sm text-surface-300">
      {#if rows.length === 0}
        <p>No class mapping set.</p>
      {:else}
        <ul>
          {#each rows as row (row.key)}
            <li>#{row.id}: {row.label}</li>
          {/each}
        </ul>
      {/if}
    </div>
  {:else}
    <div class="grid gap-2 md:grid-cols-[1fr_auto]">
      <textarea
        id="class-mapping-editor-textarea"
        class="textarea min-h-24 px-3 py-2"
        placeholder={`Paste: cat, dog, bird or JSON/JSON5 like {"cat":0, "dog":1} or {'cat': 0, 'dog': 1} or even {'0': 'cat', '1': 'dog'}`}
        bind:value={pastedText}
      ></textarea>
      <div class="btn-group self-center">
        <button type="button" class="variant-filled-primary" onclick={parseCommaSeparated}
          >Parse CSV</button
        >
        <button type="button" class="variant-filled-secondary" onclick={parseDictionary}
          >Parse JSON5</button
        >
      </div>
    </div>
    <div class="table-container">
      <table class="table table-compact w-full overflow-visible">
        <thead>
          <tr>
            <th class="w-20">ID</th>
            <th>Class</th>
            <th class="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {#each rows as row, i (row.key)}
            <tr
              class={i % 2 === 0
                ? 'bg-surface-100 hover:!bg-surface-400 dark:bg-primary-900/20 dark:hover:!bg-primary-900/50'
                : 'bg-surface-200 hover:!bg-surface-400 dark:bg-primary-900 dark:hover:!bg-primary-900/50'}
            >
              <td>
                <input
                  id={`class-mapping-editor-id-${i}`}
                  class="input w-20 px-2 py-1"
                  type="number"
                  value={row.id}
                  min={0}
                  onchange={(e) => {
                    onCellChange(i, 'id', e.currentTarget.value)
                    syncValue()
                  }}
                />
              </td>
              <td>
                <input
                  id={`class-mapping-editor-label-${i}`}
                  class="input w-full px-2 py-1"
                  type="text"
                  value={row.label}
                  oninput={(e) => onCellChange(i, 'label', e.currentTarget.value)}
                  onblur={() => syncValue()}
                />
              </td>
              <td>
                <button
                  type="button"
                  class="variant-outline-primary btn-icon"
                  title="Remove"
                  onclick={() => removeRow(i)}
                >
                  <Icon icon="iconoir:trash" />
                </button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
    <div class="text-center">
      <button type="button" class="variant-filled-surface btn" onclick={addRow}>
        <Icon icon="iconoir:plus" class="mr-1" /> Add row
      </button>
    </div>
  {/if}
</section>

<style lang="postcss">
  table.table tbody tr td {
    @apply align-middle;
  }
</style>
